import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate } from "react-router-dom";
import CustomPagination from "../../components/Shared/CustomPagination";
import AsideFilter from "./components/AsideFilter";
import "./PropertyBrowser.css";
import axios from "axios";
import { useTranslateKey } from "../../components/translation/useTranslateKey";
import { useCurrency } from "../../components/Shared/CurrencyContext";
import { API_BASE_URL } from "../../api/config";
import PropertyCard from "../../components/Shared/PropertyCard";
import { FaBorderAll } from "react-icons/fa";
import {
  faStar,
  faTags,
  faFire,
  faCrown,
} from "@fortawesome/free-solid-svg-icons";
import "./PropertyBrowser.css";


const PropertyBrowser = ({ pageData }) => {
  const t = useTranslateKey();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");
  // Parse filters from URL only on mount or reset
  const { display_max_price, max_area } = pageData?.filter_settings || {};
  const { currency } = useCurrency();
  const getInitialFilters = () => {
    const queryParams = new URLSearchParams(location.search);
    return {
      min_price: queryParams.get("min_price") || 0,
      max_price: queryParams.get("max_price") || display_max_price,
      area_min: queryParams.get("area_min") || 0,
      area_max: queryParams.get("area_max") || max_area,
      bedrooms: queryParams.get("bedrooms") || "",
      country: queryParams.get("country") || "",
      city: queryParams.get("city") || "",
      area: queryParams.get("area") || "",
      bathrooms: queryParams.get("bathrooms") || "",
      new_property_type: queryParams.getAll("new_property_type") || [],
      amenities: queryParams.getAll("amenities") || [],
      payment_plan: queryParams.getAll("payment_plan") || [],
      currency: queryParams.get("currency") || currency?.code,
      developer: queryParams.get("developer") || "",
      property_status: queryParams.getAll("property_status") || [],
      property_intent: queryParams.getAll("property_intent") || [],
    };
  };

  const [filterData, setFilterData] = useState(getInitialFilters);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch properties when URL changes (after Apply/Reset)
  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams(location.search);
        const response = await axios.get(
          `${API_BASE_URL}/properties/`,
          { params: { ...Object.fromEntries(params.entries()), currency: currency.code } }
        );
        setProperties(response.data.results);
        setTotalPages(response.data.num_pages);
        setCurrentPage(response.data.current_page);
        console.log(response.data); 
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
      setLoading(false);
    };
    fetchProperties();
  }, [location.search, currency.code]);

  // Filter toggles
  const filterToggels = [
    { name: t("all"), value: "all" },
    { name: t("hotOffer"), value: "hot" },
    { name: t("priceReduced"), value: "reduced" },
    { name: t("featured"), value: "featured" },
    { name: t("luxury"), value: "luxury" },
  ];

  // Filter button click
    const handleFilterButtonClick = (filter) => {
    setActiveFilter(filter);
    const queryParam = filter === "all" ? "" : `?filter_type=${filter}`;
    navigate(`/property-browser${queryParam}`);
  };
  // Pagination
  const handlePageChange = (event, page) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("page", page);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Apply filters (called from AsideFilter)
  const handleApplyFilters = () => {
    const params = new URLSearchParams();
    Object.entries(filterData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== "") params.append(key, v);
        });
      } else if (value !== "" && value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    params.set("currency", currency.code);
    params.set("page", 1);
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  // Reset filters (called from AsideFilter)
  const handleResetAll = () => {
    const resetData = {
      min_price: 0,
      max_price: display_max_price,
      area_min: 0,
      area_max: max_area,
      bedrooms: "",
      bathrooms: "",
      new_property_type: [],
      amenities: [],
      payment_plan: [],
      currency: currency.code,
      developer: "",
      property_status: [],
      property_intent: [],
      country: "",
      city: "",
      area: "",
    };
    setFilterData(resetData);
    const params = new URLSearchParams();
    Object.entries(resetData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== "") params.append(key, v);
        });
      } else if (value !== "" && value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    params.set("page", 1);
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

 useEffect(() => {
  setFilterData((prev) => {
    let newMin = prev.min_price;
    let newMax = prev.max_price;

    // If min/max are out of new currency's range, reset them
    if (newMin < 0 || newMin > display_max_price) newMin = 0;
    if (newMax < 0 || newMax > display_max_price) newMax = display_max_price;

    return {
      ...prev,
      currency: currency.code,
      min_price: newMin,
      max_price: newMax,
    };
  });
}, [currency.code, display_max_price]);

  return (
    <div style={{ paddingTop: 0 }}>
      <div>
        <AsideFilter
          pageData={pageData}
          display_max_price={display_max_price}
          max_area={max_area}
          currency={currency}
          filterData={filterData}
          setFilterData={setFilterData}
          onApply={handleApplyFilters}
          onReset={handleResetAll}
        />

        {/* Main Content Column */}
        <div className="flex-1 flex flex-col main-content-column">
          {/* Search Section */}
          <div className="bg-transparent border-b border-primary-accent w-full quick-filters-section">
            <div className="container mx-auto px-4">
              <div className="filters-and-search">
                <div className="filter-buttons flex-grow flex justify-between mt-4">
                  <div className="flex flex-wrap gap-3 items-center">
                    {filterToggels.map((filter) => (
                      <button
                        key={filter.value}
                        className={`filter-btn ${
                          filter.value === activeFilter ? "active" : ""
                        } flex items-center gap-1 px-3 py-1 rounded-lg`}
                        onClick={() => handleFilterButtonClick(filter.value)} //active state
                      >
                        {filter.value === "hot" ? (
                          <>
                            <FontAwesomeIcon
                              icon={faFire}
                              style={{ color: "#ff0000" }}
                            />

                            <span style={{ color: "#ff0000" }}>
                              {filter.name}
                            </span>

                          </>
                        ) : filter.value === "reduced" ? (
                          <>
                            <FontAwesomeIcon
                              icon={faTags}
                              className="text-blue-400"
                            />
                            <span>{filter.name}</span>
                          </>
                        ) : filter.value === "featured" ? (
                          <>
                            <FontAwesomeIcon
                              icon={faStar}
                              className="text-yellow-400"
                            />
                            <span>{filter.name}</span>
                          </>
                        ) : filter.value === "luxury" ? (
                          <>
                            <FontAwesomeIcon
                              icon={faCrown}
                              style={{
                                color: "#f98135",
                              }}
                            />
                            <span>{filter.name}</span>
                          </>
                        ) : (
                          <>
                            <FaBorderAll />

                            <span>{filter.name}</span>
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Properties Grid */}
          <main className="flex-1 min-h-screen bg-background/90 backdrop-blur-lg properties-container">
            <div style={{ padding: "20px" }}>
              {loading ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#11132F]">
                  <div className="loader"></div>
                </div>
              ) : properties.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {properties.map((property) => (
                    <div key={property.id} className="px-2">
                      {" "}
                      <PropertyCard
                        filters={filterData}
                        property={property}
                        projects={pageData.projects}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="col-span-3 text-center py-8">
                  <p className="text-white text-xl">{t("noProperties")}</p>
                  <button
                    onClick={handleResetAll}
                    className="mt-4 inline-block bg-accent-yellow hover:bg-primary-accent text-primary-darker hover:text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    {t("clearFilters")}
                  </button>
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PropertyBrowser;
